'use client';
import React, { useEffect, useState, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

// ─── Types ────────────────────────────────────────────────────────────────────
type RankingTab = 'challenge-wins' | 'shared-goals' | 'prayer-streaks';

interface ChallengeRankEntry {
  rank: number;
  user_id: string;
  display_name: string;
  avatar_initials: string;
  wins: number;
  total_participated: number;
  win_rate: number;
  top_category: string;
  top_category_emoji: string;
  is_current_user?: boolean;
}

interface GoalRankEntry {
  rank: number;
  user_id: string;
  display_name: string;
  avatar_initials: string;
  goals_completed: number;
  active_goals: number;
  total_days_logged: number;
  avg_completion_pct: number;
  is_current_user?: boolean;
}

interface StreakRankEntry {
  rank: number;
  user_id: string;
  display_name: string;
  avatar_initials: string;
  current_streak: number;
  longest_streak: number;
  prayers_completed: number;
  streak_tier: 'beginner' | 'consistent' | 'dedicated' | 'champion';
  is_current_user?: boolean;
}

interface CommunityStats {
  activeCompetitors: number;
  challengesWon: number;
  goalsCompleted: number;
}

// ─── Category label map ───────────────────────────────────────────────────────
const CATEGORY_LABELS: Record<string, { label: string; emoji: string }> = {
  masjid: { label: 'Masjid Prayer', emoji: '🕌' },
  charity: { label: 'Charity', emoji: '💝' },
  helping: { label: 'Helping Others', emoji: '🤝' },
  prayer: { label: 'Prayer', emoji: '🙏' },
  quran: { label: 'Quran', emoji: '📖' },
  fasting: { label: 'Fasting', emoji: '🌙' },
  dhikr: { label: 'Dhikr', emoji: '📿' },
  general: { label: 'General', emoji: '🎯' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const STREAK_TIER_CONFIG = {
  champion: { label: 'Champion', emoji: '🏆', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  dedicated: { label: 'Dedicated', emoji: '⭐', bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  consistent: { label: 'Consistent', emoji: '🔥', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  beginner: { label: 'Beginner', emoji: '🌱', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
};

function getInitials(name: string): string {
  return (name ?? 'AN')
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function getStreakTier(streak: number): StreakRankEntry['streak_tier'] {
  if (streak >= 100) return 'champion';
  if (streak >= 60) return 'dedicated';
  if (streak >= 30) return 'consistent';
  return 'beginner';
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <span className="text-2xl">🥇</span>;
  if (rank === 2) return <span className="text-2xl">🥈</span>;
  if (rank === 3) return <span className="text-2xl">🥉</span>;
  return (
    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-muted text-muted-foreground text-sm font-700">
      {rank}
    </span>
  );
}

function AvatarCircle({ initials, isCurrentUser }: { initials: string; isCurrentUser?: boolean }) {
  return (
    <div
      className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-700 flex-shrink-0 ${
        isCurrentUser ? 'ring-2 ring-primary ring-offset-2' : ''
      }`}
      style={{ background: 'linear-gradient(135deg, #0d9488, #0f766e)' }}
    >
      {initials}
    </div>
  );
}

function ProgressBar({ value, max, color = 'bg-primary' }: { value: number; max: number; color?: string }) {
  const pct = Math.min(100, Math.round((value / Math.max(1, max)) * 100));
  return (
    <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
      <div className={`${color} h-1.5 rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
    </div>
  );
}

// ─── Top 3 Podium ─────────────────────────────────────────────────────────────
function Podium({
  entries,
  getLabel,
  getSubLabel,
}: {
  entries: { rank: number; user_id: string; display_name: string; avatar_initials: string; is_current_user?: boolean }[];
  getLabel: (e: any) => string;
  getSubLabel: (e: any) => string;
}) {
  const top3 = entries.slice(0, 3);
  const order = [top3[1], top3[0], top3[2]].filter(Boolean);
  const heights = ['h-20', 'h-28', 'h-16'];
  const sizes = ['w-14 h-14 text-base', 'w-16 h-16 text-lg', 'w-12 h-12 text-sm'];

  if (top3.length === 0) return null;

  return (
    <div className="flex items-end justify-center gap-3 py-6">
      {order.map((entry, i) => {
        const isFirst = entry.rank === 1;
        return (
          <div key={entry.user_id} className="flex flex-col items-center gap-2">
            {isFirst && <span className="text-2xl">👑</span>}
            <div
              className={`rounded-full flex items-center justify-center text-white font-700 flex-shrink-0 ${sizes[i]} ${entry.is_current_user ? 'ring-2 ring-primary ring-offset-2' : ''}`}
              style={{ background: 'linear-gradient(135deg, #0d9488, #0f766e)' }}
            >
              {entry.avatar_initials}
            </div>
            <p className="text-xs font-600 text-foreground text-center max-w-[80px] truncate">{entry.display_name}</p>
            <p className="text-xs text-muted-foreground">{getLabel(entry)}</p>
            <div
              className={`w-20 rounded-t-lg flex items-center justify-center ${heights[i]} ${
                isFirst ? 'bg-amber-400/20 border-t-2 border-amber-400' : entry.rank === 2 ? 'bg-slate-300/20 border-t-2 border-slate-400' : 'bg-orange-300/20 border-t-2 border-orange-400'
              }`}
            >
              <RankBadge rank={entry.rank} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────
function EmptyRanking({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <span className="text-4xl mb-3">🏆</span>
      <p className="text-sm font-600 text-foreground">No rankings yet</p>
      <p className="text-xs text-muted-foreground mt-1">{message}</p>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function CommunityRankingsPage() {
  const [activeTab, setActiveTab] = useState<RankingTab>('prayer-streaks');
  const [challengeRanks, setChallengeRanks] = useState<ChallengeRankEntry[]>([]);
  const [goalRanks, setGoalRanks] = useState<GoalRankEntry[]>([]);
  const [streakRanks, setStreakRanks] = useState<StreakRankEntry[]>([]);
  const [communityStats, setCommunityStats] = useState<CommunityStats>({ activeCompetitors: 0, challengesWon: 0, goalsCompleted: 0 });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const supabase = createClient();

  const loadRankings = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      // ── 1. Prayer Streaks: from streaks table joined with user_profiles ──
      const { data: streakData } = await supabase
        .from('streaks')
        .select('user_id, current_streak, longest_streak, user_profiles(full_name)')
        .order('current_streak', { ascending: false })
        .limit(20);

      if (streakData) {
        const mapped: StreakRankEntry[] = streakData.map((row: any, i: number) => ({
          rank: i + 1,
          user_id: row.user_id,
          display_name: (row.user_profiles as any)?.full_name ?? 'Anonymous',
          avatar_initials: getInitials((row.user_profiles as any)?.full_name ?? 'AN'),
          current_streak: row.current_streak ?? 0,
          longest_streak: row.longest_streak ?? 0,
          prayers_completed: (row.current_streak ?? 0) * 5,
          streak_tier: getStreakTier(row.current_streak ?? 0),
          is_current_user: row.user_id === user.id,
        }));
        setStreakRanks(mapped);
      }

      // ── 2. Challenge Wins: from challenge_participants + challenges ──
      const { data: participantData } = await supabase
        .from('challenge_participants')
        .select('user_id, current_count, status, challenges(target_count, category), user_profiles(full_name)')
        .not('user_id', 'is', null);

      if (participantData) {
        const winMap: Record<string, {
          wins: number;
          total: number;
          categories: Record<string, number>;
          display_name: string;
        }> = {};

        participantData.forEach((row: any) => {
          const uid = row.user_id;
          if (!uid) return;
          if (!winMap[uid]) {
            winMap[uid] = {
              wins: 0,
              total: 0,
              categories: {},
              display_name: (row.user_profiles as any)?.full_name ?? 'Anonymous',
            };
          }
          winMap[uid].total += 1;
          const target = (row.challenges as any)?.target_count ?? 1;
          const cat = (row.challenges as any)?.category ?? 'general';
          if ((row.current_count ?? 0) >= target) {
            winMap[uid].wins += 1;
            winMap[uid].categories[cat] = (winMap[uid].categories[cat] ?? 0) + 1;
          }
        });

        const ranked: ChallengeRankEntry[] = Object.entries(winMap)
          .map(([uid, data]) => {
            const topCat = Object.entries(data.categories).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'general';
            const catInfo = CATEGORY_LABELS[topCat] ?? CATEGORY_LABELS.general;
            return {
              rank: 0,
              user_id: uid,
              display_name: data.display_name,
              avatar_initials: getInitials(data.display_name),
              wins: data.wins,
              total_participated: data.total,
              win_rate: data.total > 0 ? Math.round((data.wins / data.total) * 100) : 0,
              top_category: catInfo.label,
              top_category_emoji: catInfo.emoji,
              is_current_user: uid === user.id,
            };
          })
          .sort((a, b) => b.wins - a.wins || b.win_rate - a.win_rate)
          .slice(0, 20)
          .map((entry, i) => ({ ...entry, rank: i + 1 }));

        setChallengeRanks(ranked);
      }

      // ── 3. Shared Goals: from goal_partners + shared_goals + goal_progress ──
      const { data: partnerData } = await supabase
        .from('goal_partners')
        .select('user_id, invite_status, goal_id, shared_goals(status), user_profiles(full_name)')
        .eq('invite_status', 'accepted');

      const { data: progressData } = await supabase
        .from('goal_progress')
        .select('user_id, completed, goal_id');

      if (partnerData) {
        const goalMap: Record<string, {
          goals_completed: number;
          active_goals: number;
          display_name: string;
          goal_ids: Set<string>;
        }> = {};

        partnerData.forEach((row: any) => {
          const uid = row.user_id;
          if (!uid) return;
          if (!goalMap[uid]) {
            goalMap[uid] = {
              goals_completed: 0,
              active_goals: 0,
              display_name: (row.user_profiles as any)?.full_name ?? 'Anonymous',
              goal_ids: new Set(),
            };
          }
          const goalStatus = (row.shared_goals as any)?.status;
          if (goalStatus === 'completed') goalMap[uid].goals_completed += 1;
          else if (goalStatus === 'active') goalMap[uid].active_goals += 1;
          if (row.goal_id) goalMap[uid].goal_ids.add(row.goal_id);
        });

        // Count days logged per user
        const daysLoggedMap: Record<string, number> = {};
        (progressData ?? []).forEach((row: any) => {
          if (row.user_id && row.completed) {
            daysLoggedMap[row.user_id] = (daysLoggedMap[row.user_id] ?? 0) + 1;
          }
        });

        const goalRanked: GoalRankEntry[] = Object.entries(goalMap)
          .map(([uid, data]) => {
            const totalDays = daysLoggedMap[uid] ?? 0;
            const totalGoals = data.goals_completed + data.active_goals;
            const avgPct = totalGoals > 0 ? Math.min(100, Math.round((data.goals_completed / totalGoals) * 100)) : 0;
            return {
              rank: 0,
              user_id: uid,
              display_name: data.display_name,
              avatar_initials: getInitials(data.display_name),
              goals_completed: data.goals_completed,
              active_goals: data.active_goals,
              total_days_logged: totalDays,
              avg_completion_pct: avgPct,
              is_current_user: uid === user.id,
            };
          })
          .sort((a, b) => b.goals_completed - a.goals_completed || b.avg_completion_pct - a.avg_completion_pct)
          .slice(0, 20)
          .map((entry, i) => ({ ...entry, rank: i + 1 }));

        setGoalRanks(goalRanked);
      }

      // ── 4. Community Stats ──
      const [competitorResult, winsResult, goalsResult] = await Promise.all([
        supabase.from('challenge_participants').select('user_id', { count: 'exact', head: true }),
        supabase.from('challenge_participants').select('id', { count: 'exact', head: true }).gte('current_count', 1),
        supabase.from('shared_goals').select('id', { count: 'exact', head: true }).eq('status', 'completed'),
      ]);

      setCommunityStats({
        activeCompetitors: competitorResult.count ?? 0,
        challengesWon: winsResult.count ?? 0,
        goalsCompleted: goalsResult.count ?? 0,
      });

    } catch (err) {
      console.error('Rankings load error:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadRankings();
  }, [loadRankings]);

  const TABS: { id: RankingTab; label: string; icon: string; emoji: string }[] = [
    { id: 'prayer-streaks', label: 'Prayer Streaks', icon: 'FireIcon', emoji: '🔥' },
    { id: 'challenge-wins', label: 'Challenge Wins', icon: 'TrophyIcon', emoji: '🏆' },
    { id: 'shared-goals', label: 'Shared Goals', icon: 'FlagIcon', emoji: '🎯' },
  ];

  return (
    <AppLayout activeRoute="/community-rankings">
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-700 text-foreground">Community Rankings</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Compete, grow, and celebrate together</p>
          </div>
          <div className="flex gap-2">
            <Link
              href="/challenges"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary/10 text-primary text-sm font-600 hover:bg-primary/20 transition-colors"
            >
              <Icon name="BoltIcon" size={16} />
              <span>Challenges</span>
            </Link>
            <Link
              href="/shared-goals"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-muted text-muted-foreground text-sm font-600 hover:bg-muted/80 transition-colors"
            >
              <Icon name="FlagIcon" size={16} />
              <span>Goals</span>
            </Link>
          </div>
        </div>

        {/* Stats Banner */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Active Competitors', value: communityStats.activeCompetitors.toLocaleString(), emoji: '👥' },
            { label: 'Challenges Won', value: communityStats.challengesWon.toLocaleString(), emoji: '🏆' },
            { label: 'Goals Completed', value: communityStats.goalsCompleted.toLocaleString(), emoji: '✅' },
          ].map((stat) => (
            <div key={stat.label} className="bg-card border border-border rounded-2xl p-4 text-center shadow-card">
              <p className="text-2xl mb-1">{stat.emoji}</p>
              <p className="text-xl font-700 text-foreground">{loading ? '—' : stat.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-2xl">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-600 transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-card text-foreground shadow-card'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span>{tab.emoji}</span>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Prayer Streaks Tab */}
        {!loading && activeTab === 'prayer-streaks' && (
          <div className="space-y-4">
            {streakRanks.length === 0 ? (
              <EmptyRanking message="Be the first to build a prayer streak and claim the top spot!" />
            ) : (
              <>
                <Podium
                  entries={streakRanks}
                  getLabel={(e) => `${e.current_streak} days`}
                  getSubLabel={(e) => `${e.prayers_completed} prayers`}
                />
                <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
                  <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <h3 className="font-600 text-foreground text-sm">Full Rankings</h3>
                    <span className="text-xs text-muted-foreground">By current streak</span>
                  </div>
                  <div className="divide-y divide-border">
                    {streakRanks.map((entry) => {
                      const tier = STREAK_TIER_CONFIG[entry.streak_tier];
                      return (
                        <div
                          key={entry.user_id}
                          className={`flex items-center gap-3 px-4 py-3 transition-colors ${entry.is_current_user ? 'bg-primary/5' : 'hover:bg-muted/50'}`}
                        >
                          <div className="w-8 flex items-center justify-center flex-shrink-0">
                            <RankBadge rank={entry.rank} />
                          </div>
                          <AvatarCircle initials={entry.avatar_initials} isCurrentUser={entry.is_current_user} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={`text-sm font-600 truncate ${entry.is_current_user ? 'text-primary' : 'text-foreground'}`}>
                                {entry.display_name}
                                {entry.is_current_user && <span className="ml-1 text-xs font-500 text-primary">(You)</span>}
                              </p>
                              <span className={`text-xs px-1.5 py-0.5 rounded-full border font-500 ${tier.bg} ${tier.text} ${tier.border}`}>
                                {tier.emoji} {tier.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 mt-1">
                              <ProgressBar value={entry.current_streak} max={streakRanks[0]?.current_streak ?? 1} color="bg-orange-400" />
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-700 text-foreground">{entry.current_streak}🔥</p>
                            <p className="text-xs text-muted-foreground">{entry.longest_streak} best</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Challenge Wins Tab */}
        {!loading && activeTab === 'challenge-wins' && (
          <div className="space-y-4">
            {challengeRanks.length === 0 ? (
              <EmptyRanking message="Join challenges and complete them to appear on the leaderboard!" />
            ) : (
              <>
                <Podium
                  entries={challengeRanks}
                  getLabel={(e) => `${e.wins} wins`}
                  getSubLabel={(e) => `${e.win_rate}% win rate`}
                />
                <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
                  <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <h3 className="font-600 text-foreground text-sm">Full Rankings</h3>
                    <span className="text-xs text-muted-foreground">By challenge wins</span>
                  </div>
                  <div className="divide-y divide-border">
                    {challengeRanks.map((entry) => (
                      <div
                        key={entry.user_id}
                        className={`flex items-center gap-3 px-4 py-3 transition-colors ${entry.is_current_user ? 'bg-primary/5' : 'hover:bg-muted/50'}`}
                      >
                        <div className="w-8 flex items-center justify-center flex-shrink-0">
                          <RankBadge rank={entry.rank} />
                        </div>
                        <AvatarCircle initials={entry.avatar_initials} isCurrentUser={entry.is_current_user} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm font-600 truncate ${entry.is_current_user ? 'text-primary' : 'text-foreground'}`}>
                              {entry.display_name}
                              {entry.is_current_user && <span className="ml-1 text-xs font-500 text-primary">(You)</span>}
                            </p>
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-500">
                              {entry.top_category_emoji} {entry.top_category}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <ProgressBar value={entry.wins} max={challengeRanks[0]?.wins ?? 1} color="bg-amber-400" />
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-700 text-foreground">{entry.wins} 🏆</p>
                          <p className="text-xs text-muted-foreground">{entry.win_rate}% win rate</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Win Rate Insight */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
              <span className="text-2xl">💡</span>
              <div>
                <p className="text-sm font-600 text-amber-800">Boost your ranking</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Join active challenges and log your progress daily to climb the leaderboard. Consistency beats intensity!
                </p>
                <Link href="/challenges" className="inline-flex items-center gap-1 mt-2 text-xs font-600 text-amber-700 hover:text-amber-900">
                  Browse Challenges <Icon name="ArrowRightIcon" size={12} />
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Shared Goals Tab */}
        {!loading && activeTab === 'shared-goals' && (
          <div className="space-y-4">
            {goalRanks.length === 0 ? (
              <EmptyRanking message="Create shared goals with accountability partners to appear here!" />
            ) : (
              <>
                <Podium
                  entries={goalRanks}
                  getLabel={(e) => `${e.goals_completed} goals`}
                  getSubLabel={(e) => `${e.avg_completion_pct}% avg`}
                />
                <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
                  <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <h3 className="font-600 text-foreground text-sm">Full Rankings</h3>
                    <span className="text-xs text-muted-foreground">By goals completed</span>
                  </div>
                  <div className="divide-y divide-border">
                    {goalRanks.map((entry) => (
                      <div
                        key={entry.user_id}
                        className={`flex items-center gap-3 px-4 py-3 transition-colors ${entry.is_current_user ? 'bg-primary/5' : 'hover:bg-muted/50'}`}
                      >
                        <div className="w-8 flex items-center justify-center flex-shrink-0">
                          <RankBadge rank={entry.rank} />
                        </div>
                        <AvatarCircle initials={entry.avatar_initials} isCurrentUser={entry.is_current_user} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`text-sm font-600 truncate ${entry.is_current_user ? 'text-primary' : 'text-foreground'}`}>
                              {entry.display_name}
                              {entry.is_current_user && <span className="ml-1 text-xs font-500 text-primary">(You)</span>}
                            </p>
                            {entry.active_goals > 0 && (
                              <span className="text-xs px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200 font-500">
                                {entry.active_goals} active
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <ProgressBar value={entry.avg_completion_pct} max={100} color="bg-teal-500" />
                            <span className="text-xs text-muted-foreground flex-shrink-0">{entry.avg_completion_pct}%</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-700 text-foreground">{entry.goals_completed} ✅</p>
                          <p className="text-xs text-muted-foreground">{entry.total_days_logged} days logged</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Accountability nudge */}
            <div className="bg-teal-50 border border-teal-200 rounded-2xl p-4 flex items-start gap-3">
              <span className="text-2xl">🤝</span>
              <div>
                <p className="text-sm font-600 text-teal-800">Accountability drives results</p>
                <p className="text-xs text-teal-700 mt-0.5">
                  Members with accountability partners complete goals 3× more often. Start a shared goal today!
                </p>
                <Link href="/shared-goals" className="inline-flex items-center gap-1 mt-2 text-xs font-600 text-teal-700 hover:text-teal-900">
                  Create a Shared Goal <Icon name="ArrowRightIcon" size={12} />
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
